// Display formatting for limit rows.
//
// Separate from ../format.js on purpose: that file formats CAPTURES, where
// every measure is a signed delta and dropping the sign turns a removal into an
// addition. Nothing here is signed — a usage total and a threshold are both
// absolute — and sharing one formatter would mean one of the two callers asking
// for the wrong behaviour by default.
//
// The helpers mirror datum's routes/dashboard/format.js, which renders the same
// rows from the point owner's side. The two apps cannot import from each other,
// so the duplication is deliberate; keep them reading the same way.

const UNITS = ["B", "KB", "MB", "GB", "TB", "PB"];

// Unsigned. The one place a minus could legitimately appear is a usage total
// over a window that net-shrank, and it is passed through rather than hidden.
export const formatBytes = (value) => {
  if (value === null || value === undefined) return "—";
  const number = Number(value);
  if (!Number.isFinite(number)) return "—";
  if (number === 0) return "0 B";

  const sign = number < 0 ? "-" : "";
  let magnitude = Math.abs(number);
  let unit = 0;

  while (magnitude >= 1024 && unit < UNITS.length - 1) {
    magnitude /= 1024;
    unit += 1;
  }

  const rendered = unit === 0 ? String(Math.round(magnitude)) : magnitude.toFixed(1);
  return `${sign}${rendered} ${UNITS[unit]}`;
};

export const formatPercent = (ratio) => {
  if (ratio === null || ratio === undefined) return "—";
  const pct = ratio * 100;
  // A non-zero usage must never round to "0%" — that is the display reading as
  // "nothing consumed" when something was.
  if (pct > 0 && pct < 1) return "<1%";
  return `${Math.round(pct)}%`;
};

// The period, as something to put beside a number rather than an enum value.
const PERIOD_LABEL = {
  annually: "this year",
  monthly: "this month",
  weekly: "this week",
  daily: "today",
};

export const periodLabel = (period) => PERIOD_LABEL[period] || String(period || "");

// A limit's own name for itself. The description is what an operator set; the
// key is the fallback and says which measure over which period.
export const limitLabel = ({ limitDescription, measure, period }) =>
  limitDescription || `${measure} ${periodLabel(period)}`.trim();

const STATUS_LABEL = {
  // In datum's enum and implemented by nothing (D6). Saying so is the whole
  // reason the status exists — the alternative is reporting 0 and looking like
  // headroom.
  "period-unsupported": "Period not supported",
  // The aggregate failed, or came back partial without this bucket's group in
  // it. Distinct from zero and distinct from unsupported.
  unavailable: "Usage unavailable",
};

export const statusLabel = ({ status, limit, breached, ratio }) => {
  if (status && status !== "ok") return STATUS_LABEL[status] || status;
  if (limit === null || limit === undefined) return "No threshold set";
  // "Over limit", never "blocked": nothing in this platform enforces a limit
  // yet. What enforcement should do on breach is still an open decision.
  if (breached) return "Over limit";
  if (ratio !== null && ratio >= 0.9) return "Near limit";
  return "Within limit";
};

// Bootstrap contextual colours, ordered by how much attention the row wants.
export const limitColor = ({ status, breached, ratio }) => {
  if (status && status !== "ok") return "secondary";
  if (breached) return "danger";
  if (ratio === null || ratio === undefined) return "secondary";
  if (ratio >= 0.9) return "warning";
  if (ratio >= 0.75) return "info";
  return "success";
};

// Whether there is a bar to draw at all.
//
// Both halves matter. A non-ok status has no number; a null ratio has a number
// but nothing to measure it against — an absent threshold, or one datum stored
// as a string that does not parse. Neither may render as an empty bar, because
// an empty bar reads as 0% used.
export const isMeasurable = ({ status, ratio }) =>
  status === "ok" && ratio !== null && ratio !== undefined;

// Clamped for the bar only. An overrun fills it rather than overflowing the
// container — and the true percentage is always printed beside it, because a
// bar pinned at 100% cannot distinguish "just over" from "four times over".
export const barValue = (ratio) => Math.min(100, Math.max(0, (ratio || 0) * 100));
